<script setup lang="ts">
import {
  type IService,
  ServiceTypes,
  type ITimeslot,
  type ICompany,
  type ITarget,
  ServiceFormats,
  ServiceBlockBookingTypes
} from '@types';
import SlotsCalendarMonthSlider from './header/SlotsCalendarMonthSlider.vue';
import SlotsCalendarDaySlider from './header/SlotsCalendarDaySlider.vue';
import SlotsCalendarDay from './SlotsCalendarDay.vue';
import SlotsCalendarDaySlots from './SlotsCalendarDaySlots.vue';
import { useSlotSelect } from './core/useSlotSelect';
import { type Cell, genCells, type SlotDate } from '@sub/components/calendar/core/Cell';
import { getPeriod } from '@sub/components/calendar/core/Period';
import {
  addDays,
  addWeeks, formatSlotTime,
  getDaysPeriod,
  getTimezoneName,
  isDatesEqual,
  teleportSlotToTz,
  useTimeZone
} from '@sub/utils/times';
import type { TimeslotSettings } from '@sub/utils/getCompanySettings';
import { mergeDisabled, type SlotAvailability } from '@sub/components/calendar/core/Disabled';
import { ServicePriceLib } from '@sub/components/calendar/CuePriceLib/ServicePriceLib';
import { Calendar } from '@sub/components/calendar/core/Calendar';
import { getDisabledFromOrderedSlots } from '@sub/components/calendar/useOrderedTimeSlots';
import { getServiceMinLength } from '@sub/utils/CuePriceLib2/service';
import { useDayScrollHandler } from '@sub/components/service/useDayScrollHandler';
import type { TimeSlot } from '@sub/components/calendar/TimeSlot';

const props = withDefaults(defineProps<{
  company?: ICompany;
  service?: IService;
  orderedTimeSlots?: ITimeslot[];
  target?: ITarget['id'];
  showTimezone?: boolean;
  timeslotSettings?: TimeslotSettings;
}>(), {
  orderedTimeSlots: () => [],
  showTimezone: true,
  timeslotSettings: () => ({}),
});

const selected = defineModel<Cell>('selected');
function getIsSelected(date: SlotDate) {
  return !!selected.value && (+selected.value.from <= +date.from) && (+date.to <= +selected.value.to)
}

const address = computed(() => props.service.addresses?.[0]);
const timeOffset = computed(() => (props.service.startTimeOffset || 0));

const periods = computed(() => address.value?.workingTime?.map(
  (time) => getPeriod({ days: time.day, start: +time.timeStart + timeOffset.value, end: +time.timeEnd + timeOffset.value }),
) || []);

const bookingTimezone = computed(() => address.value?.isOnline ? undefined : address.value?.timezone);
const bookingTimezoneName = computed(() => props.showTimezone ? getTimezoneName(bookingTimezone.value) : '');

const disabled = computed(() => {
  const tz = address.value?.timezone;
  const orderedTimeSlotsWithTimezone = props.orderedTimeSlots.map(s => ({ ...s, start: useTimeZone(s.start, tz), end: useTimeZone(s.end, tz) }));
  return getDisabledFromOrderedSlots(orderedTimeSlotsWithTimezone, props.service)
});

function minutesToMilliseconds(minutes: number) {
  return minutes * 60 * 1000;
}

function getBlockBooking(service: IService): number {
  if (service.blockBookingIsEnable) {
    const amount = +service.blockBookingTime;

    return minutesToMilliseconds(service.blockBookingType === ServiceBlockBookingTypes.m ? amount : amount * 60);
  }

  return 0;
}

const cueCalendar = computed(() => new Calendar({
  timezone: address.value?.timezone,
  deadLineAdd: getBlockBooking(props.service),
  disabled: disabled.value,
  periods: periods.value,
  timeOffset: props.service.startTimeOffset || 0,
}));

const minLength = computed<number>(() => getServiceMinLength(props.service));
const fixLength = computed(() => [ServiceTypes.Individual, ServiceTypes.GroupService].includes(props.service?.type) ? props.service?.timeLength || 0 : false);
const step = computed(() => fixLength.value ? fixLength.value : props.company.calendarTimeStep);

const cueCalendarSelect = useSlotSelect(cueCalendar, selected, { minTimeLength: minLength, allowBetweenDays: true });

const today = computed(() => useTimeZone(new Date(), bookingTimezone.value));

/** Максимальное ограничение в 1 год */
const maxDate = computed(() => addDays(today.value, props.service.availableBookingHorizon || 365));

const dateTapeStart = ref<Date>(today.value);
watch(today, () => {
  dateTapeStart.value = today.value;
});

const focusDay = ref<Date>(dateTapeStart.value);

const tapeDays = ref<Date[]>([]);
watch(dateTapeStart, () => {
  tapeDays.value = getNextSlotsDays(dateTapeStart.value);
}, { immediate: true });

function getNextSlotsDays(firstDay = addDays(tapeDays.value[tapeDays.value.length - 1], 1)) {
  if (maxDate.value && firstDay > maxDate.value) return [];
  const lastDay = addWeeks(firstDay, 2);
  return getDaysPeriod(firstDay, maxDate.value ? minDate([lastDay, maxDate.value]) : lastDay);
}

const { resetScroll, scrollContainerRef, slotsRef } = useDayScrollHandler(tapeDays, focusDay, () => {
  const nextSlotsDays = getNextSlotsDays();
  if (nextSlotsDays.length > 0) tapeDays.value = [...tapeDays.value, ...nextSlotsDays];
});

function onSelectDate(d: Date) {
  dateTapeStart.value = d;
  focusDay.value = d;
  // nextTick(() => scrollTo(d));
  resetScroll();
}

const servicePriceLib = computed(() => new ServicePriceLib(props.service));

function getPrice(date: SlotDate) {
  const options: Parameters<ServicePriceLib['getParamPrice']>[0] = {
    dateStart: date.from,
    dateEnd: date.to,
    isFree: minLength.value < step.value,
    metaPriority: false,
    amountPeople: props.service.type !== ServiceTypes.Individual ? undefined : +props.service.amountPeopleMin,
    target: props.target,
  }

  const param = servicePriceLib.value?.getParamPrice(options)
  const freeParam = param || servicePriceLib.value?.getParamPrice({ ...options, isFree: true })
  const { general: price, all = [] } = param?.calcParamPrice({ duration: minLength.value || step.value }) || {}
  const errorMaxTime = options.dateStart.getDate() !== new Date(+options.dateEnd - step.value * 60000).getDate()
  const dynamic = (errorMaxTime || all?.length < 1) && freeParam
  const isFree = props.service.format === ServiceFormats.without

  return {
    priceError: !isFree && (!param?.blocks[0] || !isDatesEqual(date.from, param.blocks[0].from) || !isDatesEqual(date.to, param.blocks[param.blocks.length - 1].to)),
    price,
    isFree,
    param,
    dynamic
  }
}

const i18n = useI18n();
const i18nLocales = computed(() => ({ dynamicPrice: i18n.t('block.date.time.list.slot.element.caption.text.dynamic'), basePrice: i18n.t('word.from.money.base') }));
const { formatPrice } = usePriceFormatter();

function getDisabledChecker(date: SlotDate, _disabled = disabled.value) {
  cueCalendar.value.setDisabled(_disabled)
  // cueCalendar.value.setPeriods(periodsWithOffset.value || [])
  const { disabled, findInDisabledList } = cueCalendar.value.isCellDisabled(date)
  return {
    disabled,
    disabledListItem: findInDisabledList(false)?.dis
  }
}

function getDateKey(day: Date) {
  return `${day.getDate()}.${day.getMonth()}.${day.getFullYear()}`
}

function genDayTimeslots(res: Record<string, TimeSlot[]>, date: Date) {
  const cells = genCells({
    step: step.value,
    date: date,
    periods: periods.value,
  });
  const mergedDisabled = mergeDisabled(disabled.value, cells, cueCalendar.value, minLength.value);

  cells.forEach(date => {
    const { price = 0, param, dynamic, isFree, priceError } = getPrice(date)
    if (priceError) return;

    const tzDate = address.value?.isOnline ? teleportSlotToTz(date, address.value?.timezone) : date;
    const text = fixLength.value ? formatTime(tzDate.from) : formatSlotTime(tzDate);

    const disabledChecker = getDisabledChecker(date, mergedDisabled)
    const isDisabled = disabledChecker.disabled || (!param?.blocks.length && !isFree)

    const slot = {
      date,
      disabled: isDisabled,
      selected: getIsSelected(date),
      slotAvailability: disabledChecker.disabledListItem?.availability,
      text,
      priceText: dynamic && !isFree ? i18nLocales.value.dynamicPrice : price ? `${i18nLocales.value.basePrice} ${formatPrice(price)}` : '',
    } satisfies TimeSlot;

    const dateKey = getDateKey(tzDate.from);
    if (!res[dateKey]) res[dateKey] = [];
    res[dateKey].push(slot);
  });
  return res;
}

const slotsByDays = computed(() => {
  return tapeDays.value.reduce(genDayTimeslots, genDayTimeslots({}, addDays(tapeDays.value[0], -1)));
});

const emit = defineEmits(['update:slotAvailability']);

function onSelectSlot({ date, slotAvailability }: { date: SlotDate; slotAvailability?: SlotAvailability; }, isShift: boolean) {
  if (!cueCalendarSelect || !date.from || !date.to) return

  if (fixLength.value && !cueCalendarSelect.check(date)) cueCalendarSelect.clear()

  cueCalendarSelect.select(date, {
    mode: isShift ? 'range' : 'single',
    overload: props.service.type !== ServiceTypes.Rent,
  })

  emit('update:slotAvailability', slotAvailability);
}

const is12Hour = computed(() => is12HourFormat());
</script>

<template lang="pug">
.slots-calendar.w-100p.p-0-16
  .calendar-header.p-8-0.z-index-5
    SlotsCalendarMonthSlider(
      v-model:focus="focusDay"
      :minDate="today"
      :maxDate="maxDate"
      @update:focus="onSelectDate"
    )
    SlotsCalendarDaySlider(
      v-model:focus="focusDay"
      :minDate="today"
      :maxDate="maxDate"
      @update:focus="onSelectDate"
    )
  .calendar-slots.flex.col-8.overflow-y-auto.scrollbar-hide.position-relative(ref="scrollContainerRef")
    SlotsCalendarDay(
      v-for="day in tapeDays"
      ref="slotsRef"
      :key="getDateKey(day)"
      :day="day"
      :timezone="bookingTimezoneName"
    )
      SlotsCalendarDaySlots(
        :slots="slotsByDays[getDateKey(day)]"
        :timeslotSettings="timeslotSettings"
        :is12HourFormat="is12Hour"
        @select="onSelectSlot"
      )
</template>

<style scoped lang="scss">
.slots-calendar {
  @screen md {
    --uno: overflow-hidden;
  }
}

.calendar-header {
  background: #ffffff;
  position: sticky;
  top: 42px;

  @screen md {
    z-index: unset;
    position: unset;
  }
}

.calendar-slots {
  height: calc(100% - 114px);
}
</style>